import React, { useState, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import requestClient from '../../utils/requestClient';
import Loader from '../../components/Elements/Loader';
import Metric from '../../components/Elements/Metrics/Metric';
import MetricChange from '../../components/Elements/Metrics/MetricChange';
import ChatbotSection from '../../components/Sections/ChatbotSection';
import { pillarMapper } from '../../utils/pillarMapper';
import { resourceMapper } from '../../utils/resourceMapper';
import FormSelect from '../../components/Elements/Form/FormSelect';

const Overview = ({ }) => {
    const [data, setData] = useState(null);
    const [company, setCompany] = useState(null);
    const [newsletterReport, setNewsletterReport] = useState(null);
    const [period, setPeriod] = useState('this_month');

    const navigate = useNavigate();
    const location = useLocation();

    const dateOptions = [
        { value: 'this_month', label: 'This Month' },
        { value: 'previous_month', label: 'Last Month' }
    ];

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const selectedPeriod = params.get('period') || 'this_month';
        setPeriod(selectedPeriod);

        async function getData() {
            const response = await requestClient().get(`/api/v2/employer/dashboard?period=${selectedPeriod}`);
            setData(response.data.data);
        }
        getData();
    }, [location.search]);

    useEffect(() => {
        async function getCompany() {
            const response = await requestClient().get('/api/v2/employer/company/');
            setCompany(response.data.data);
        }
        getCompany();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const selectedPeriod = params.get('period') || 'this_month';

        async function getNewsletterReport() {
            const response = await requestClient().get(`/api/v2/employer/report/newsletters?period=${selectedPeriod}`);
            setNewsletterReport(response.data.data);
        }
        getNewsletterReport();
    }, [location.search]);

    const currentPeriodLabel = dateOptions.find(option => option.value === period)?.label || '';

    const handlePeriodChange = (event) => {
        const newPeriod = event.target.value;
        setPeriod(newPeriod);
        navigate(`?period=${newPeriod}`);
    };

    return (data ?
        <main>
            <section className='mt-8 mb-8'>
                <div className='container'>
                    <div className="flex mb-4 items-center">
                        <div>
                            <h1>Wellbeing Snapshot</h1>
                            <div className='prose'>
                                <p>Here is a snapshot of the average wellbeing across your organisation <strong className='lowercase'>{currentPeriodLabel}</strong>.</p>
                            </div>
                        </div>
                        <div className='ml-auto'>
                            <FormSelect background='bg-white' value={period} options={dateOptions} onChange={handlePeriodChange} />
                        </div>
                    </div>
                    <div className="grid gap-2 lg:grid-cols-2">
                        {Object.entries(data.current.report.wellbeing).map(([name, value], index) => (
                            <Metric key={index} title={pillarMapper(name).title} icon={pillarMapper(name).icon('h-[50px]')}>
                                <MetricChange value={value.toFixed(1)} change={data.changes.report.wellbeing[name]} />
                            </Metric>
                        ))}
                        <Metric title={"Overall Wellbeing Score"} titleSize={'h3'} className={'lg:col-span-2'}>
                            <MetricChange value={data.current.report.wellbeing_score.toFixed(1)} change={data.changes.report.wellbeing_score.toFixed(1)} />
                        </Metric>
                    </div>
                </div>
            </section>       
            <ChatbotSection 
                userType={'employer'}
                name={'Harmony'} 
                title={(<span><span className="font-normal">Have Questions? </span><br />Chat with Harmony, Our Assistant Wellbeing Officer</span>)} 
                topics={[
                    // 'What is our most improved wellbeing pillar this month?',
                    'Ideas for helping improve workplace wellbeing',
                    "Can you help me understand what my organisation's wellbeing scores mean?"
                ]}
                description={(
                    <span>Harmony is here to help you get the most out of OptiMe.<br />
                    Ask about platform features, wellbeing metrics, employee engagement insights, or how to support your team's growth.<br />
                    Start by typing your question, or ask Harmony to walk you through your latest wellbeing trends.</span>)
                } 
            />
            <section className='mt-8 mb-8'>
                <div className='container'>
                    <div className="flex mb-4 items-center">
                        <div>
                            <h2 className='mb-1'>Sentiment</h2>
                            <div className='prose'>
                                <p>Find out more about how your employees are feeling <strong className='lowercase'>{currentPeriodLabel}</strong>.</p>
                            </div>
                        </div>
                        <div className='ml-auto'>
                            <FormSelect background='bg-white' value={period} options={dateOptions} onChange={handlePeriodChange} />
                        </div>
                    </div>
                    <div className="grid gap-2 lg:grid-cols-2">
                        <Metric title={'Highest Sentiments'} description={'Positive words which come up the most frequently.'}>
                            {data.current.report.top_positive_sentiment_words.length > 0 ?
                                (<span className={`text-[20px] text-green leading-snug`}>
                                {data.current.report.top_positive_sentiment_words.map((word, index) => (
                                    <span key={index} className={'block'}>{word.word} <span className='text-xs'>({word.title})</span></span>
                                ))}
                            </span>) : (<span className={`text-[30px] leading-snug`}>N/a</span>)}
                        </Metric>
                        <Metric title={'Lowest Sentiments'} description={'Negative words which come up the most frequently.'}>
                            {data.current.report.top_negative_sentiment_words.length > 0 ?
                                (<span className={`text-[20px] text-red leading-snug`}>
                                {data.current.report.top_negative_sentiment_words.map((word, index) => (
                                    <span key={index} className={'block'}>{word.word} <span className='text-xs'>({word.title})</span></span>
                                ))}
                            </span>) : (<span className={`text-[30px] leading-snug`}>N/a</span>)}
                        </Metric>
                    </div>
                </div>
            </section>
            <section className='mb-8'>
                <div className='container'>
                    <div className="flex mb-4 items-center">
                        <div>
                            <h2 className='mb-1'>Engagement</h2>
                            <div className='prose'>
                                <p>Find out how engaged your team are with your wellness initiatives <strong className='lowercase'>{currentPeriodLabel}</strong>.</p>
                            </div>
                        </div>
                        <div className='ml-auto'>
                            <FormSelect background='bg-white' value={period} options={dateOptions} onChange={handlePeriodChange} />
                        </div>
                    </div>
                    <div className="grid gap-2 lg:grid-cols-2">
                        <Metric title={'Total Users'} description={'How many users have been invited.'}>
                            <span className={`text-[45px] text-blue`}>{data.current.report.activation_rate.total}</span>
                        </Metric>
                        <Metric title={'Accounts Activated'} description={'How many users have engaged with the platform.'}>
                            <span className={`text-[45px] text-blue`}>{data.current.report.activation_rate.active}</span>
                        </Metric>
                        <Metric title={'Newsletter Opens'} description={'How many users have opened newsletters.'}>
                            <MetricChange value={newsletterReport ? newsletterReport.current.report.open_rate + '%' : '0%'} change={newsletterReport ? newsletterReport.changes.report.open_rate : 0} />
                        </Metric>
                        <Metric title={'Newsletter Clicks'} description={'How many users have clicked on newsletters.'}>
                            <MetricChange value={newsletterReport ? newsletterReport.current.report.click_rate + '%' : '0%'} change={newsletterReport ? newsletterReport.changes.report.click_rate : 0} />
                        </Metric>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            {Object.keys(data.current.report.top_resources).length > 0 && (
                <section className='mb-8'>
                    <div className='container'>
                        <div className="flex mb-4 items-center">
                            <div>
                                <h2 className='mb-1'>Content</h2>
                                <div className='prose'>
                                    <p>Find out which content your team are most interested in.</p>
                                </div>
                            </div>
                            <div className='ml-auto'>
                                <FormSelect background='bg-white' value={period} options={dateOptions} onChange={handlePeriodChange} />
                            </div>
                        </div>

                        <div className="grid gap-2 lg:grid-cols-2">
                            {Object.entries(data.current.report.top_resources).map(([type, resources]) => (
                                <Metric
                                    key={type}
                                    title={`Top ${resourceMapper(type).singular}`}
                                    description={`Most viewed ${resourceMapper(type).singular} content.`}
                                >
                                    <span className="text-sm leading-snug text-blue">
                                        {resources.length > 0
                                            ? <a href={resources[0].url}>{resources[0].title} ({pillarMapper(resources[0].pillar).title})</a>
                                            : 'N/a'}
                                    </span>
                                </Metric>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </main>
        : <Loader className="my-8" text="Loading your dashboard" />);
};

export default Overview;
